/**
 * Карточка работы в кабинете — блок `WEB-3.1`, требования 009, 307, 310, 311,
 * 312, 313, 320, 321, долг `TD-63`, спецификация §2 и §5 (`EU3`…`EU30`).
 *
 * Клиент отображает, сервер решает. Кнопка стоит ровно при действии ответа:
 * «Подтвердить» — при элементе `available_actions` со статусом завершения, отмена
 * и спор — при значении `claim_actions`, ссылка отслеживания — при стороне
 * заказчика (своего действия у неё в контракте нет), правка файла — при его
 * признаках. Статус с ролью нигде не сравнивается. Цена — только `price`:
 * поля нет — нет и строки; `listing_price` её не подменяет (`FR-BE-716`).
 *
 * Чат закрывается не по статусу: признака «только чтение» у чата работы в
 * контракте нет, и поле уходит после отказа сервера с кодом конфликта (`EU10`).
 *
 * Ответы снятого экрана не применяются (`TD-63`): после `dispose` каждый поздний
 * ответ останавливается проверкой `live()`, а таймер трека и карта гаснут. Запрос
 * при этом не обрывается — механизм отмены незавершённой загрузки в треке один и
 * принадлежит оболочке (`TD-39`).
 *
 * Обновление раз в минуту касается только раздела карты: трек и признак связи.
 * Остальное человек мог править, и перерисовка под рукой ему помешала бы.
 *
 * PIN живёт в одной переменной замыкания и только до повтора, отзыва или ухода
 * с экрана: ни в адрес, ни в хранилище, ни в журнал он не попадает (`EU20`).
 *
 * Разметка — блок `WEB-D.1`, решения `Б6`, `Б7`, `Б8`, `Б15`: разделы лежат в трёх
 * областях (шапка, основной столбец, боковой), id работы — строкой после них.
 * Условия разделов и порядок внутри области прежние.
 */

import { ApiFailure, UNAUTHENTICATED_CODE, newIdempotencyKey } from '../api/client';
import type { ApiClient, MessageSubmission } from '../api/client';
import { nextPageCursor } from '../api/contract';
import type {
  Dispute,
  ErrorDetail,
  Message,
  PageEnvelope,
  TrackingLink,
  Work,
  WorkAssignmentRecord,
  WorkClaimAction,
  WorkFile,
  WorkFileKind,
  WorkPhoto,
  WorkTrack,
} from '../api/contract';
import { WORK_FILE_KINDS } from '../api/generated/enums';
import {
  activeLocale,
  bodyTypeStringKey,
  cancellationCategoryStringKey,
  cancellationRequestStatusStringKey,
  disputeAuthorStringKey,
  disputeOutcomeStringKey,
  disputeStatusStringKey,
  guiltySideStringKey,
  peregonVehicleKindStringKey,
  vehicleKindStringKey,
  workFileKindStringKey,
  workPhotoPhaseStringKey,
  workSignalStringKey,
  workStatusStringKey,
} from '../i18n';
import type { Strings } from '../i18n';
import { workAssignmentPath, worksPath } from '../router';
import {
  createFieldMessageSlot,
  dateLabel,
  enumLabel,
  momentTimeLabel,
  moneyLabel,
  refusalText,
  renderFieldMessages,
  renderScreenState,
  screenStateFor,
  VALIDATION_FAILED_CODE,
  workInternalMark,
  workStatusPill,
} from './state';
import type { ScreenState } from './state';
import { INTERNAL_CANCEL_ACTION } from './internal-work-form';
import { ASSIGN_ACTION } from './work-assignment';
import type { MapPoint, WorkTrackMapOptions } from './work-track-map';

/** То, что карточке нужно от карты; настоящая — `work-track-map.ts`. */
export interface WorkCardMap {
  show(points: readonly MapPoint[], last: MapPoint | null): void;
  destroy(): void;
}

export interface WorkCardScreenOptions {
  readonly client: ApiClient;
  readonly strings: Strings;
  readonly companyId: string;
  readonly workId: string;
  readonly createMap?: (container: HTMLElement, options: WorkTrackMapOptions) => WorkCardMap;
  readonly onUnauthenticated?: () => void;
}

export interface WorkCardScreen {
  readonly element: HTMLElement;
  /** Состояние экрана либо `null` — содержимое показано. */
  readonly state: ScreenState | null;
  load(): Promise<void>;
  /** Подписи на новом языке; набранное в формах остаётся на месте. */
  relabel(next: Strings): void;
  /** Снятие экрана: таймер и карта гаснут, поздние ответы не применяются. */
  dispose(): void;
}

/** Статус действия, которым сервер предлагает подтвердить доставку. */
const CONFIRM_ACTION: Work['status'] = 'COMPLETED';
const CUSTOMER_SIDE: NonNullable<Work['side']> = 'customer';
const CONFLICT_CODE = 'CONFLICT';
const TRACK_PERIOD_MS = 60_000;
const SECONDS_IN_MINUTE = 60;
const RATING_SCORES = [1, 2, 3, 4, 5] as const;
const PHOTO_ACCEPT = 'image/*';

/** Узел с классом и текстом. */
function node<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] {
  const made = document.createElement(tag);

  made.className = className;

  if (className === 'notice') {
    made.setAttribute('role', 'alert');
  }

  if (text !== undefined) {
    made.textContent = text;
  }

  return made;
}

/** Поле формы с подписью и местом для сообщения сервера. */
function field(caption: string, control: HTMLElement): HTMLLabelElement {
  const label = node('label', 'form-field');

  label.append(node('span', 'form-field__caption', caption), control, createFieldMessageSlot());

  return label;
}

function textArea(name: string): HTMLTextAreaElement {
  const area = document.createElement('textarea');

  area.name = name;

  return area;
}

function option(value: string, text: string): HTMLOptionElement {
  const made = document.createElement('option');

  made.value = value;
  made.textContent = text;

  return made;
}

/** Ссылка сервера протухла: срок прислал он же, клиент только читает. */
function urlExpired(moment: string): boolean {
  return new Date(moment).getTime() <= Date.now();
}

/** Текст отказа у полей формы; чему места нет — общим текстом рядом. */
function showRefusal(form: HTMLElement, notice: HTMLElement, error: unknown, strings: Strings): void {
  const details: readonly ErrorDetail[] = error instanceof ApiFailure ? error.details : [];
  const homeless = renderFieldMessages(form, details);
  const texts: string[] = [];

  for (const detail of homeless) {
    texts.push(detail.message);
  }

  notice.textContent = details.length === 0 ? refusalText(error, strings) : texts.join(' ');
}

/** Ключ попытки и содержимое, для которого он выдан. */
interface IdempotentAttempt {
  readonly value: string;
  readonly content: string;
}

/** Отпечаток отправляемого: файл различается именем, размером и временем. */
function attemptContent(parts: ReadonlyArray<string | File | null>): string {
  return JSON.stringify(parts.map((part) => (part instanceof File ? [part.name, part.size, part.lastModified] : part)));
}

/**
 * Ключ живёт только для повтора того же содержимого: с другим телом и прежним
 * ключом сервер ответил бы прежней записью, а новая молча пропала бы.
 */
function attemptFor(previous: IdempotentAttempt | null, content: string): IdempotentAttempt {
  return previous?.content === content ? previous : { value: newIdempotencyKey(), content };
}

interface MessagePaneSource {
  readonly formName: string;
  list(options?: { cursor?: string }): Promise<PageEnvelope<Message>>;
  send(input: MessageSubmission, key: string): Promise<Message>;
  mark(lastId: string): Promise<void>;
}

export function createWorkCardScreen(options: WorkCardScreenOptions): WorkCardScreen {
  const { companyId, workId, onUnauthenticated } = options;
  let strings = options.strings;
  let disposed = false;

  /**
   * Отвергнутый токен посреди работы ведёт на вход из любого обращения экрана:
   * место одно, а не по условию в каждой ветке отказа. Экран при этом снимается —
   * таймер без токена ходить не должен.
   */
  const client = new Proxy(options.client, {
    get(target, name): unknown {
      const value: unknown = Reflect.get(target, name);

      if (typeof value !== 'function') {
        return value;
      }

      return async (...args: unknown[]): Promise<unknown> => {
        try {
          return await (value as (...given: unknown[]) => unknown).apply(target, args);
        } catch (error) {
          if (error instanceof ApiFailure && error.code === UNAUTHENTICATED_CODE && !disposed) {
            dispose();
            onUnauthenticated?.();
          }

          throw error;
        }
      };
    },
  });

  const element = node('section', 'screen screen-work-card');
  const body = node('div', 'work-card');

  let state: ScreenState | null = 'loading';
  let work: Work | null = null;
  /** Сколько раз работа заменена ответом действия: опоздавший опрос её не откатит. */
  let workWrites = 0;
  const live = (): boolean => !disposed;

  /** Что сделать, когда сменился язык: панели держат свои подписи сами. */
  const relabels: Array<() => void> = [];

  function setWork(next: Work): void {
    work = next;
    workWrites += 1;
  }

  function section(name: string): HTMLElement {
    const made = node('section', 'work-card__section');

    made.dataset.section = name;

    return made;
  }

  const sections = {
    head: section('status'),
    actions: section('actions'),
    parties: section('parties'),
    assignment: section('assignment'),
    cargo: section('cargo'),
    map: section('map'),
    chat: section('chat'),
    thread: section('driver-thread'),
    files: section('files'),
    photos: section('photos'),
    tracking: section('tracking'),
    rating: section('rating'),
    claims: section('claims'),
    history: section('history'),
  };

  /**
   * Набранное в формах, собираемых заново, переживает перерисовку: разделы
   * рисуются из ответа, а черновик человека ответу не принадлежит.
   */
  function withDrafts(root: HTMLElement, draw: () => void): void {
    const drafts = new Map<string, string>();

    for (const control of root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('form [name]')) {
      if (control.getAttribute('type') !== 'file') {
        drafts.set(`${control.form?.dataset.form ?? ''}/${control.name}`, control.value);
      }
    }

    const focused = document.activeElement;
    const focusKey = focused instanceof HTMLElement && root.contains(focused) && 'form' in focused && 'name' in focused
      ? `${(focused.form as HTMLFormElement | null)?.dataset.form ?? ''}/${String(focused.name)}`
      : null;

    draw();

    for (const control of root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('form [name]')) {
      const key = `${control.form?.dataset.form ?? ''}/${control.name}`;
      const kept = drafts.get(key);

      if (kept !== undefined && control.getAttribute('type') !== 'file') {
        control.value = kept;
      }

      // Перерисовка по минутному обновлению не вынимает курсор из поля.
      if (key === focusKey) {
        control.focus();
      }
    }
  }

  // ── Статус, стороны, транспорт, груз, история — целиком из ответа ──────────

  function fact(caption: string, value: string, name?: string): HTMLElement {
    const row = node('p', 'work-card__fact');

    row.append(node('span', 'work-card__caption', caption), node('span', '', value));

    if (name !== undefined) {
      row.dataset.field = name;
    }

    return row;
  }

  function renderStatus(shown: Work): void {
    const statusRow = node('p', 'work-card__fact');

    statusRow.append(node('span', 'work-card__caption', strings.work_card_status), workStatusPill(strings, workStatusStringKey(shown.status)));

    if (shown.internal) {
      statusRow.append(workInternalMark(strings));
    }

    const nodes: HTMLElement[] = [node('h1', '', shown.title), statusRow];

    // Нет ключа и `null` — одно и то же: строки суммы нет (`FR-BE-716`).
    if (shown.price != null) {
      nodes.push(fact(strings.work_card_price, moneyLabel(strings, shown.price), 'price'));
    }

    if (shown.vehicle_kind !== null) {
      nodes.push(fact(strings.work_card_vehicle, enumLabel(strings, peregonVehicleKindStringKey(shown.vehicle_kind))));
    }

    if (shown.vehicle_model !== null) {
      nodes.push(fact(strings.work_card_vehicle_model, shown.vehicle_model));
    }

    if (shown.auto_completed) {
      nodes.push(node('p', 'work-card__note', strings.work_card_auto_completed));
    }

    sections.head.replaceChildren(...nodes);
  }

  const actionNotice = node('p', 'notice');
  let confirming = false;

  actionNotice.setAttribute('role', 'alert');

  function offered(shown: Work, status: Work['status']): boolean {
    for (const action of shown.available_actions) {
      if (action.status === status) {
        return true;
      }
    }

    return false;
  }

  async function confirmDelivery(): Promise<void> {
    if (confirming || !window.confirm(strings.work_card_confirm_question)) {
      return;
    }

    confirming = true;
    actionNotice.textContent = '';

    try {
      const saved = await client.changeWorkStatus(workId, { status: CONFIRM_ACTION, no_show: false });

      if (live()) {
        setWork(saved);
      }
    } catch (error) {
      if (live()) {
        actionNotice.textContent = refusalText(error, strings);
        await rereadWork();
      }
    } finally {
      confirming = false;
    }

    if (live()) {
      renderWork();
    }
  }

  // Отмена внутреннего заказа (`Б11`): `internal` выбирает форму, право называет
  // сервер элементом действий. Узлы формы постоянные: набранное и сообщения сервера
  // переживают перерисовку раздела.
  const closingReason = textArea('reason');
  const closingCaption = node('span', 'form-field__caption');
  const closingField = node('label', 'form-field');
  const closingSend = node('button', 'action action--danger');
  const closingForm = node('form', 'form');
  let closingOpen = false;
  let closing = false;

  closingField.append(closingCaption, closingReason, createFieldMessageSlot());
  closingSend.type = 'submit';
  closingForm.dataset.form = 'internal-cancel';
  closingForm.id = 'internal-cancel-form';
  closingForm.noValidate = true;
  closingForm.append(closingField, closingSend);
  closingForm.addEventListener('submit', (event) => {
    event.preventDefault();
    void closeInternal();
  });

  async function closeInternal(): Promise<void> {
    if (closing || !window.confirm(strings.work_card_internal_cancel_question)) {
      return;
    }

    closing = true;
    actionNotice.textContent = '';
    renderFieldMessages(closingForm, []);

    try {
      const saved = await client.changeWorkStatus(workId, { status: INTERNAL_CANCEL_ACTION, reason: closingReason.value, no_show: false });

      if (live()) {
        setWork(saved);
        closingOpen = false;
        closingReason.value = '';
      }
    } catch (error) {
      if (live()) {
        // `422` судит введённое: форма остаётся. Иной отказ значит, что сменилась сама
        // работа, и действие могло уйти, даже если сервер приложил подробности.
        if (error instanceof ApiFailure && error.code === VALIDATION_FAILED_CODE) {
          showRefusal(closingForm, actionNotice, error, strings);
        } else {
          actionNotice.textContent = refusalText(error, strings);
          await rereadWork();
        }
      }
    } finally {
      closing = false;
    }

    if (live()) {
      renderWork();
    }
  }

  function renderActions(shown: Work): boolean {
    const nodes: HTMLElement[] = [];

    if (offered(shown, CONFIRM_ACTION)) {
      const confirm = node('button', 'action action--primary', strings.work_card_confirm);

      confirm.type = 'button';
      confirm.addEventListener('click', () => {
        void confirmDelivery();
      });
      nodes.push(confirm);

      if (shown.confirm_by !== null) {
        nodes.push(fact(strings.work_card_confirm_by, momentTimeLabel(activeLocale(), shown.confirm_by), 'confirm-by'));
      }
    }

    if (offered(shown, ASSIGN_ACTION)) {
      // Основная кнопка на экране одна: при «Подтвердить» назначение остаётся ссылкой.
      const link = node(
        'a',
        offered(shown, CONFIRM_ACTION) ? 'company-link' : 'action action--primary',
        shown.assignment === null ? strings.works_assign : strings.works_replace,
      );

      link.href = workAssignmentPath(companyId, workId);
      nodes.push(link);
    }

    if (shown.internal && offered(shown, INTERNAL_CANCEL_ACTION)) {
      const closeButton = node('button', 'action action--danger', strings.work_card_internal_cancel);

      closeButton.type = 'button';
      closeButton.setAttribute('aria-expanded', String(closingOpen));
      closeButton.setAttribute('aria-controls', closingForm.id);
      closeButton.addEventListener('click', () => {
        closingOpen = !closingOpen;

        // Свёрнутая форма не хранит отказ: сообщение снимается, набранная причина остаётся.
        if (!closingOpen) {
          renderFieldMessages(closingForm, []);
          actionNotice.textContent = '';
        }

        renderWork();
      });
      nodes.push(closeButton);

      if (closingOpen) {
        closingCaption.textContent = strings.work_card_cancel_reason;
        closingSend.textContent = strings.work_card_internal_cancel_send;
        nodes.push(closingForm);
      }
    } else {
      closingOpen = false;
      renderFieldMessages(closingForm, []);
    }

    sections.actions.replaceChildren(...nodes, actionNotice);

    return nodes.length > 0 || actionNotice.textContent !== '';
  }

  function verifiedLabel(verified: boolean): string {
    return verified ? strings.work_card_verified : strings.work_card_not_verified;
  }

  function renderParties(shown: Work): boolean {
    // `Б10`: у заказа стороны — одна фирма, а создатель стороной не является (`CR-042` п. 10).
    if (shown.internal) {
      return false;
    }

    const nodes: HTMLElement[] = [];
    const add = (
      caption: string,
      company: Work['customer_company'],
      person: Work['customer'],
    ): void => {
      const parts: string[] = [];

      if (company !== null) {
        parts.push(company.name ?? strings.work_assignment_unnamed, verifiedLabel(company.documents_verified));
      }

      if (person !== null) {
        parts.push(person.name ?? strings.work_assignment_unnamed);

        if (person.phone !== null) {
          parts.push(person.phone);
        }

        if (company === null) {
          parts.push(verifiedLabel(person.documents_verified));
        }
      }

      if (parts.length > 0) {
        nodes.push(fact(caption, parts.join(' · ')));
      }
    };

    add(strings.work_card_customer, shown.customer_company, shown.customer);
    add(strings.work_card_executor, shown.executor_company, shown.executor);
    sections.parties.replaceChildren(node('h2', '', strings.work_card_parties), ...nodes);

    return nodes.length > 0;
  }

  function renderAssignment(shown: Work): void {
    const nodes: HTMLElement[] = [node('h2', '', strings.work_card_assignment)];
    const { assignment } = shown;

    if (assignment === null) {
      nodes.push(node('p', 'work-card__note', strings.work_card_assignment_empty));
    } else {
      const { tractor, trailer, driver } = assignment;

      nodes.push(fact(enumLabel(strings, vehicleKindStringKey(tractor.kind)), tractor.vin));

      if (trailer !== null) {
        nodes.push(fact(enumLabel(strings, vehicleKindStringKey(trailer.kind)), trailer.vin));
      }

      nodes.push(
        fact(
          strings.work_assignment_driver,
          [driver.name ?? strings.work_assignment_unnamed, driver.phone, verifiedLabel(driver.documents_verified)].join(' · '),
        ),
      );
    }

    sections.assignment.replaceChildren(...nodes);
  }

  function renderCargo(shown: Work): boolean {
    const { cargo } = shown;

    if (cargo === null) {
      return false;
    }

    sections.cargo.replaceChildren(
      node('h2', '', strings.work_card_cargo),
      fact(strings.work_card_cargo_type, cargo.cargo_type),
      fact(strings.work_card_cargo_loading, dateLabel(activeLocale(), cargo.loading_at)),
      fact(strings.work_card_cargo_weight, `${cargo.weight_kg} ${strings.cargo_listing_kg}`),
      fact(strings.work_card_cargo_body, enumLabel(strings, bodyTypeStringKey(cargo.body_type))),
    );

    return true;
  }

  function historyRecord(record: WorkAssignmentRecord): HTMLElement {
    const locale = activeLocale();
    const { tractor, trailer, driver } = record;
    const towed = trailer === null ? [] : [trailer.vin];

    return node(
      'li',
      'work-card__row',
      [
        tractor.vin,
        ...towed,
        driver.name ?? strings.work_assignment_unnamed,
        `${strings.work_assignment_assigned_by}: ${record.assigned_by.name ?? strings.work_assignment_unnamed} ${momentTimeLabel(locale, record.assigned_at)}`,
        `${strings.work_assignment_replaced_by}: ${record.replaced_by.name ?? strings.work_assignment_unnamed} ${momentTimeLabel(locale, record.replaced_at)}`,
      ].join(' · '),
    );
  }

  function renderHistory(shown: Work): void {
    const rows: HTMLElement[] = [];

    for (const entry of shown.status_history) {
      const parts = [enumLabel(strings, workStatusStringKey(entry.to)), momentTimeLabel(activeLocale(), entry.created_at)];

      if (entry.reason !== null) {
        parts.push(entry.reason);
      }

      rows.push(node('li', 'work-card__row', parts.join(' · ')));
    }

    for (const record of shown.assignment_history) {
      rows.push(historyRecord(record));
    }

    const list = node('ul', 'work-card__list');

    list.replaceChildren(...rows);
    sections.history.replaceChildren(
      node('h2', '', strings.work_card_history),
      rows.length === 0 ? node('p', 'work-card__note', strings.work_card_history_empty) : list,
    );
  }

  // ── Трек и карта ────────────────────────────────────────────────────────────

  const mapHeading = node('h2', '');
  const mapPlace = node('div', 'work-card__map');
  const trackCaptions = node('div', 'work-card__captions');
  let map: WorkCardMap | null = null;
  let track: WorkTrack | null = null;
  let trackFailure: string | null = null;
  let tilesFailed = false;
  let polling = false;
  let timer: ReturnType<typeof setInterval> | null = null;

  function renderTrackCaptions(): void {
    const captions: HTMLElement[] = [];

    if (trackFailure !== null) {
      captions.push(node('p', 'notice', trackFailure));
    }

    if (track === null && trackFailure === null) {
      captions.push(renderScreenState('loading', strings));
    }

    if (track !== null && track.points.length === 0) {
      captions.push(node('p', 'work-card__note', strings.work_card_track_empty));
    }

    if (track?.thinned === true) {
      captions.push(node('p', 'work-card__note', strings.work_card_track_thinned));
    }

    if (work?.signal != null) {
      captions.push(fact(strings.work_card_track_signal, enumLabel(strings, workSignalStringKey(work.signal))));
    }

    if (work?.last_position_age_seconds != null) {
      const minutes = Math.floor(work.last_position_age_seconds / SECONDS_IN_MINUTE);

      captions.push(fact(strings.work_card_track_age, `${minutes} ${strings.work_card_track_minutes}`));
    }

    if (tilesFailed) {
      captions.push(node('p', 'notice', strings.work_card_tiles_failed));
    }

    trackCaptions.replaceChildren(...captions);
  }

  function renderMapSection(): void {
    mapHeading.textContent = strings.work_card_track;

    // Контейнер карты живёт на месте: перестановка сбросила бы ей размер.
    if (mapPlace.parentElement !== sections.map) {
      sections.map.replaceChildren(mapHeading, mapPlace, trackCaptions);
    }

    renderTrackCaptions();
  }

  function showTrack(): void {
    if (track !== null) {
      mapPlace.hidden = track.points.length === 0 && track.last_position === null;
      map?.show(track.points, track.last_position);
    }
  }

  /**
   * Код карты подгружается при первом открытии карточки: Leaflet не нужен ни
   * входу, ни публичной странице. Сбой подгрузки — та же подпись, что и у подложки.
   */
  async function attachMap(): Promise<void> {
    if (map !== null) {
      return;
    }

    const onTilesFailed = (): void => {
      if (live()) {
        tilesFailed = true;
        renderTrackCaptions();
      }
    };

    try {
      const create = options.createMap ?? (await import('./work-track-map')).createWorkTrackMap;

      if (live()) {
        map = create(mapPlace, { onTilesFailed });
        showTrack();
      }
    } catch {
      onTilesFailed();
    }
  }

  async function readTrack(): Promise<void> {
    try {
      const received = await client.getWorkTrack(workId);

      if (!live()) {
        return;
      }

      track = received;
      trackFailure = null;
      showTrack();
    } catch (error) {
      if (live()) {
        trackFailure = refusalText(error, strings);
      }
    }

    if (live()) {
      renderTrackCaptions();
    }
  }

  /** Работа без того, что меняется само собой с ходом времени. */
  function meaningOf(shown: Work): string {
    return JSON.stringify({ ...shown, signal: null, last_position: null, last_position_at: null, last_position_age_seconds: null });
  }

  async function tick(): Promise<void> {
    if (polling || disposed || document.visibilityState !== 'visible') {
      return;
    }

    polling = true;

    // Работа перечитывается и применяется целиком: сдачу груза логист видит без
    // перезагрузки. Разделы рисуются заново, только если ответ изменился не одной
    // давностью, — черновики и фокус переживают перерисовку, чат и карта на месте.
    const seenWrites = workWrites;
    const fresh = client.getWork(workId).then(
      async (received) => {
        if (!live() || work === null || seenWrites !== workWrites || confirming || claimBusy) {
          return;
        }

        const changed = meaningOf(received) !== meaningOf(work);
        const disputeBefore = JSON.stringify(dispute);

        work = received;
        await followDispute();

        if (live() && (changed || JSON.stringify(dispute) !== disputeBefore)) {
          renderWork();
        }
      },
      () => undefined,
    );

    await Promise.all([readTrack(), fresh]);
    polling = false;

    if (live()) {
      renderTrackCaptions();
    }
  }

  // ── Чат и нить — одна панель над двумя источниками ──────────────────────────

  interface MessagePane {
    readonly root: HTMLElement;
    read(): Promise<void>;
  }

  function createMessagePane(source: MessagePaneSource, readOnly: boolean): MessagePane {
    const root = node('div', 'work-card__pane');
    const list = node('ul', 'work-card__messages');
    const more = node('button', 'action');
    const notice = node('p', 'notice');
    const form = document.createElement('form');
    const text = textArea('body');
    const photo = document.createElement('input');
    const submit = node('button', 'action');
    let messages: readonly Message[] = [];
    let cursor: string | null = null;
    let failure: string | null = null;
    let answered = false;
    let closed = readOnly;
    let sending = false;
    let key: IdempotentAttempt | null = null;
    let mediaRefreshed = false;
    let loadingMore = false;

    form.className = 'form';
    form.noValidate = true;
    form.dataset.form = source.formName;
    notice.setAttribute('role', 'alert');
    more.type = 'button';
    submit.type = 'submit';
    photo.type = 'file';
    photo.name = 'file';
    photo.accept = PHOTO_ACCEPT;

    const textField = node('span', '');
    const photoField = node('span', '');

    form.append(textField, photoField, submit);

    function caption(): void {
      textField.replaceChildren(field(strings.work_card_message_text, text));
      photoField.replaceChildren(field(strings.work_card_message_photo, photo));
      submit.textContent = strings.work_card_message_send;
      more.textContent = strings.work_card_messages_more;
    }

    function row(message: Message): HTMLElement {
      const item = node('li', message.mine ? 'work-card__message work-card__message--mine' : 'work-card__message');

      item.append(
        node(
          'span',
          'work-card__caption',
          `${message.author.name ?? strings.work_assignment_unnamed} · ${momentTimeLabel(activeLocale(), message.created_at)}`,
        ),
      );

      // Скрытое модератором и удалённое приходит без содержимого.
      if (message.hidden || message.deleted) {
        item.append(node('span', 'work-card__note', strings.work_card_message_hidden));

        return item;
      }

      if (message.body !== null && message.body !== '') {
        item.append(node('span', 'work-card__message-text', message.body));
      }

      if (message.file !== null) {
        const media = message.kind === 'voice' ? document.createElement('audio') : document.createElement('img');

        media.src = message.file.url;

        if (media instanceof HTMLAudioElement) {
          media.controls = true;
        } else {
          media.alt = strings.work_card_message_photo;
          media.className = 'work-card__image';
        }

        // Подписанная ссылка живёт недолго: отказ носителя перечитывает страницу.
        media.addEventListener('error', () => {
          if (!mediaRefreshed) {
            mediaRefreshed = true;
            void read();
          }
        });
        item.append(media);
      }

      return item;
    }

    function draw(): void {
      // Новые приходят первыми, рисуется снизу вверх — порядок ответа наоборот,
      // без сортировки.
      const rows: HTMLElement[] = [];

      for (const message of messages) {
        rows.unshift(row(message));
      }

      list.replaceChildren(...rows);

      const nodes: HTMLElement[] = [];

      if (cursor !== null) {
        nodes.push(more);
      }

      if (!answered && failure === null) {
        nodes.push(renderScreenState('loading', strings));
      } else if (rows.length === 0 && failure === null) {
        nodes.push(node('p', 'work-card__note', strings.work_card_messages_empty));
      } else {
        nodes.push(list);
      }

      nodes.push(notice);

      if (failure !== null) {
        notice.textContent = failure;
      }

      if (!closed) {
        nodes.push(form);
      }

      root.replaceChildren(...nodes);
    }

    async function read(): Promise<void> {
      try {
        const page = await source.list();

        if (!live()) {
          return;
        }

        messages = page.data;
        cursor = nextPageCursor(page.meta);
        answered = true;

        if (failure !== null) {
          notice.textContent = '';
        }

        failure = null;
      } catch (error) {
        if (live()) {
          failure = refusalText(error, strings);
        }
      }

      if (live()) {
        draw();

        const newest = messages[0];

        if (failure === null && newest !== undefined) {
          // Показ не ждёт отметки: чтение страницы прочтения не отмечает, отметка —
          // отдельным вызовом, и ни её задержка, ни отказ содержимому не мешают.
          void source.mark(newest.id).catch(() => undefined);
        }
      }
    }

    more.addEventListener('click', () => {
      if (cursor === null || loadingMore) {
        return;
      }

      loadingMore = true;
      more.disabled = true;
      void source.list({ cursor }).then(
        (page) => {
          if (live()) {
            messages = [...messages, ...page.data];
            cursor = nextPageCursor(page.meta);
            draw();
          }
        },
        (error: unknown) => {
          if (live()) {
            notice.textContent = refusalText(error, strings);
          }
        },
      ).finally(() => {
        loadingMore = false;
        more.disabled = false;
      });
    });

    form.addEventListener('submit', (event) => {
      event.preventDefault();

      if (sending) {
        return;
      }

      void send();
    });

    async function send(): Promise<void> {
      const chosen = photo.files?.[0] ?? null;
      // Лишнее для вида поле сервер отвергает: с фото текст остаётся в поле.
      const input: MessageSubmission = chosen === null
        ? { kind: 'text', body: text.value, file: null }
        : { kind: 'photo', body: null, file: chosen };

      sending = true;
      submit.disabled = true;
      notice.textContent = '';
      renderFieldMessages(form, []);
      key = attemptFor(key, attemptContent([input.kind, input.body ?? '', chosen]));

      try {
        const saved = await source.send(input, key.value);

        if (!live()) {
          return;
        }

        key = null;
        messages = [saved, ...messages];

        if (chosen === null) {
          text.value = '';
        }

        photo.value = '';
        draw();
      } catch (error) {
        if (!live()) {
          return;
        }

        const code = error instanceof ApiFailure ? error.code : null;

        // Отказ-ответ сервера закрывает эту попытку: повтор — новая запись.
        if (code !== null) {
          key = null;
        }

        if (code === CONFLICT_CODE) {
          closed = true;
          draw();
        }

        showRefusal(form, notice, error, strings);
      } finally {
        sending = false;
        submit.disabled = false;
      }
    }

    caption();
    draw();
    relabels.push(() => {
      caption();
      draw();
    });

    return { root, read };
  }

  const chatPane = createMessagePane(
    {
      formName: 'chat',
      list: async (paging) => await client.listWorkMessages(workId, paging),
      send: async (input, key) => await client.sendWorkMessage(workId, input, key),
      mark: async (lastId) => {
        await client.markWorkChatRead(workId, lastId);
      },
    },
    false,
  );

  let threadPane: MessagePane | null = null;
  const threadHeading = node('h2', '');
  const threadCaption = node('p', 'work-card__note');
  let threadOpen = false;

  function renderThread(shown: Work): boolean {
    const thread = shown.driver_thread;

    if (thread === null) {
      return false;
    }

    threadHeading.textContent = strings.work_card_thread;
    threadCaption.textContent = strings.work_card_thread_caption;

    // Открытая панель остаётся на месте: перестановка остановила бы голосовое.
    if (threadOpen && threadPane?.root.parentElement === sections.thread) {
      return true;
    }

    const nodes: HTMLElement[] = [threadHeading, threadCaption];

    if (threadOpen && threadPane !== null) {
      nodes.push(threadPane.root);
    } else {
      const open = node('button', 'action', strings.work_card_thread_open);
      const unread = node('span', 'work-card__badge', String(thread.unread_count));

      unread.dataset.field = 'unread';
      open.type = 'button';
      open.addEventListener('click', () => {
        threadPane ??= createMessagePane(
          {
            formName: 'thread',
            list: async (paging) => await client.listDriverThreadMessages(thread.id, paging),
            send: async (input, key) => await client.sendDriverThreadMessage(thread.id, input, key),
            mark: async (lastId) => {
              await client.markDriverThreadRead(thread.id, lastId);
            },
          },
          thread.read_only,
        );
        threadOpen = true;
        renderWork();
        void threadPane.read();
      });
      if (thread.unread_count > 0) {
        nodes.push(unread);
      }

      nodes.push(open);
    }

    sections.thread.replaceChildren(...nodes);

    return true;
  }

  // ── Файлы и фото ────────────────────────────────────────────────────────────

  const filesList = node('ul', 'work-card__list');
  const filesNotice = node('p', 'notice');
  const filesForm = document.createElement('form');
  const fileKind = document.createElement('select');
  const fileDescription = textArea('description');
  const fileInput = document.createElement('input');
  const filesLoading = node('div', '');
  let files: readonly WorkFile[] | null = null;
  let filesReadOnly = false;
  let filesFailure: string | null = null;
  let uploading = false;
  let uploadKey: IdempotentAttempt | null = null;
  let editingFile: string | null = null;

  filesForm.className = 'form';
  filesForm.noValidate = true;
  filesForm.dataset.form = 'file';
  filesForm.addEventListener('submit', (event) => {
    event.preventDefault();
  });
  filesNotice.setAttribute('role', 'alert');
  fileKind.name = 'kind';
  fileInput.type = 'file';
  fileInput.name = 'file';

  async function readFiles(): Promise<void> {
    try {
      const received = await client.listWorkFiles(workId);

      if (!live()) {
        return;
      }

      files = received.data;
      filesReadOnly = received.read_only;
      filesFailure = null;
    } catch (error) {
      if (live()) {
        filesFailure = refusalText(error, strings);
      }
    }

    if (live()) {
      renderFiles();
    }
  }

  async function openFile(file: WorkFile): Promise<void> {
    await readFiles();

    if (!live()) {
      return;
    }

    for (const fresh of files ?? []) {
      if (fresh.id === file.id) {
        window.open(fresh.url, '_blank', 'noopener');
      }
    }
  }

  async function saveFileDescription(file: WorkFile, form: HTMLFormElement, value: string): Promise<void> {
    try {
      const description = value === '' ? null : value;

      await client.updateWorkFile(workId, file.id, { description });

      if (live()) {
        editingFile = null;
        await readFiles();
      }
    } catch (error) {
      if (live()) {
        showRefusal(form, filesNotice, error, strings);
      }
    }
  }

  async function deleteFile(file: WorkFile): Promise<void> {
    if (!window.confirm(strings.work_card_file_delete_question)) {
      return;
    }

    try {
      await client.deleteWorkFile(workId, file.id);

      if (live()) {
        await readFiles();
      }
    } catch (error) {
      if (live()) {
        filesNotice.textContent = refusalText(error, strings);
      }
    }
  }

  function fileRow(file: WorkFile): HTMLElement {
    const row = node('li', 'work-card__row');
    const open = node('a', 'company-link', file.name);

    open.href = file.url;
    open.target = '_blank';
    open.rel = 'noopener';
    open.dataset.action = 'open-file';
    open.addEventListener('click', (event) => {
      if (urlExpired(file.url_expires_at)) {
        event.preventDefault();
        void openFile(file);
      }
    });
    row.append(open, node('span', 'work-card__caption', enumLabel(strings, workFileKindStringKey(file.kind))));

    if (file.description !== null) {
      row.append(node('span', 'work-card__note', file.description));
    }

    if (file.editable && editingFile === file.id) {
      const form = document.createElement('form');
      const area = textArea('description');
      const save = node('button', 'action', strings.work_card_file_save);

      form.className = 'form';
      form.noValidate = true;
      form.dataset.form = `file-edit-${file.id}`;
      area.value = file.description ?? '';
      save.type = 'submit';
      form.append(field(strings.work_card_file_description, area), save);
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        void saveFileDescription(file, form, area.value);
      });
      row.append(form);
    } else if (file.editable) {
      const edit = node('button', 'action', strings.work_card_file_edit);

      edit.type = 'button';
      edit.addEventListener('click', () => {
        editingFile = file.id;
        renderFiles();
      });
      row.append(edit);
    }

    if (file.deletable) {
      const remove = node('button', 'action action--danger', strings.work_card_file_delete);

      remove.type = 'button';
      remove.addEventListener('click', () => {
        void deleteFile(file);
      });
      row.append(remove);
    }

    return row;
  }

  function renderFiles(): void {
    withDrafts(filesList, () => {
      const rows: HTMLElement[] = [];

      for (const file of files ?? []) {
        rows.push(fileRow(file));
      }

      filesList.replaceChildren(...rows);
    });

    const nodes: HTMLElement[] = [node('h2', '', strings.work_card_files)];

    if (files === null && filesFailure === null) {
      filesLoading.replaceChildren(renderScreenState('loading', strings));
      nodes.push(filesLoading);
    } else if (filesFailure !== null) {
      nodes.push(node('p', 'notice', filesFailure));
    } else if (files?.length === 0) {
      nodes.push(node('p', 'work-card__note', strings.work_card_files_empty));
    }

    nodes.push(filesList, filesNotice);

    if (!filesReadOnly) {
      const chosenKind = fileKind.value;
      const choices: HTMLOptionElement[] = [];

      for (const kind of WORK_FILE_KINDS) {
        choices.push(option(kind, enumLabel(strings, workFileKindStringKey(kind))));
      }

      fileKind.replaceChildren(...choices);

      if (chosenKind !== '') {
        fileKind.value = chosenKind;
      }

      filesForm.replaceChildren(
        field(strings.work_card_file_kind, fileKind),
        field(strings.work_card_file_description, fileDescription),
        field(strings.work_card_file_choose, fileInput),
      );
      nodes.push(filesForm);
    }

    sections.files.replaceChildren(...nodes);
  }

  fileInput.addEventListener('change', () => {
    const chosen = fileInput.files?.[0];

    if (chosen === undefined || uploading) {
      return;
    }

    void upload(chosen);
  });

  async function upload(chosen: File): Promise<void> {
    uploading = true;
    // Занятое поле не принимает второй файл: иначе выбор пропал бы молча.
    fileInput.disabled = true;
    filesNotice.textContent = strings.work_card_file_uploading;
    renderFieldMessages(filesForm, []);

    const entered = fileDescription.value.trim();
    const description = entered === '' ? null : entered;

    uploadKey = attemptFor(uploadKey, attemptContent([fileKind.value, entered, chosen]));

    try {
      await client.uploadWorkFile(workId, { kind: fileKind.value as WorkFileKind, file: chosen, description }, uploadKey.value);

      if (!live()) {
        return;
      }

      uploadKey = null;
      fileInput.value = '';
      fileDescription.value = '';
      await readFiles();
      filesNotice.textContent = strings.work_card_file_uploaded;
    } catch (error) {
      if (live()) {
        if (error instanceof ApiFailure && error.code !== null) {
          uploadKey = null;
        }

        // Тот же файл можно выбрать снова: без сброса браузер не пришлёт `change`.
        fileInput.value = '';
        showRefusal(filesForm, filesNotice, error, strings);
      }
    } finally {
      uploading = false;
      fileInput.disabled = false;
    }
  }

  let photos: readonly WorkPhoto[] | null = null;
  let photosFailure: string | null = null;

  async function readPhotos(): Promise<void> {
    try {
      const received = await client.listWorkPhotos(workId);

      if (!live()) {
        return;
      }

      photos = received;
      photosFailure = null;
    } catch (error) {
      if (live()) {
        photosFailure = refusalText(error, strings);
      }
    }

    if (live()) {
      renderPhotos();
    }
  }

  function photoFigure(photo: WorkPhoto): HTMLElement {
    const figure = node('figure', 'work-card__photo');
    const link = node('a', '');
    const image = document.createElement('img');

    image.src = photo.url;
    image.alt = enumLabel(strings, workPhotoPhaseStringKey(photo.phase));
    image.className = 'work-card__image';
    link.href = photo.url;
    link.target = '_blank';
    link.rel = 'noopener';
    link.append(image);
    link.addEventListener('click', (event) => {
      if (urlExpired(photo.url_expires_at)) {
        event.preventDefault();
        void readPhotos();
      }
    });
    figure.append(link);

    if (photo.plate) {
      figure.dataset.plate = 'true';
      figure.append(node('figcaption', 'work-card__caption', strings.work_card_photo_plate));
    }

    return figure;
  }

  function renderPhotos(): void {
    const nodes: HTMLElement[] = [node('h2', '', strings.work_card_photos)];
    const groups = new Map<string, HTMLElement>();

    for (const photo of photos ?? []) {
      let group = groups.get(photo.phase);

      if (group === undefined) {
        group = node('div', 'work-card__photo-group');
        group.dataset.phase = photo.phase;
        group.append(node('h3', '', enumLabel(strings, workPhotoPhaseStringKey(photo.phase))));
        groups.set(photo.phase, group);
        nodes.push(group);
      }

      group.append(photoFigure(photo));
    }

    if (photosFailure !== null) {
      nodes.push(node('p', 'notice', photosFailure));
    } else if (photos === null) {
      nodes.push(renderScreenState('loading', strings));
    } else if (photos.length === 0) {
      nodes.push(node('p', 'work-card__note', strings.work_card_photos_empty));
    }

    sections.photos.replaceChildren(...nodes);
  }

  // ── Ссылка отслеживания ─────────────────────────────────────────────────────

  let shownLink: TrackingLink | null = null;
  let trackingBusy = false;
  const trackingNotice = node('p', 'notice');

  trackingNotice.setAttribute('role', 'alert');

  async function createLink(): Promise<void> {
    if (trackingBusy) {
      return;
    }

    trackingBusy = true;
    trackingNotice.textContent = '';

    try {
      const created = await client.createTrackingLink(workId);

      if (live()) {
        shownLink = created;
      }
    } catch (error) {
      if (live()) {
        trackingNotice.textContent = refusalText(error, strings);
      }
    } finally {
      trackingBusy = false;
    }

    if (live() && work !== null) {
      renderTracking(work);
    }
  }

  async function revokeLink(): Promise<void> {
    if (trackingBusy || !window.confirm(strings.work_card_tracking_revoke_question)) {
      return;
    }

    trackingBusy = true;
    trackingNotice.textContent = '';

    try {
      await client.revokeTrackingLink(workId);

      if (live()) {
        shownLink = null;
        trackingNotice.textContent = strings.work_card_tracking_revoked;
      }
    } catch (error) {
      if (live()) {
        trackingNotice.textContent = refusalText(error, strings);
      }
    } finally {
      trackingBusy = false;
    }

    if (live() && work !== null) {
      renderTracking(work);
    }
  }

  function renderTracking(shown: Work): boolean {
    // Действия в контракте нет: показ по стороне, которую назвал сервер (`EU19`).
    if (shown.side !== CUSTOMER_SIDE) {
      return false;
    }

    const create = node('button', 'action', strings.work_card_tracking_create);
    const revoke = node('button', 'action action--danger', strings.work_card_tracking_revoke);
    const nodes: HTMLElement[] = [node('h2', '', strings.work_card_tracking)];

    create.type = 'button';
    revoke.type = 'button';
    create.addEventListener('click', () => {
      void createLink();
    });
    revoke.addEventListener('click', () => {
      void revokeLink();
    });

    if (shownLink !== null) {
      const { url, pin } = shownLink;
      const copy = node('button', 'action', strings.work_card_tracking_copy);

      copy.type = 'button';
      copy.addEventListener('click', () => {
        // Вне защищённого адреса буфера у браузера нет вовсе.
        const copied = (navigator.clipboard as Clipboard | undefined)?.writeText(`${url} ${pin}`) ?? Promise.reject(new Error('clipboard'));

        void copied.then(
          () => {
            trackingNotice.textContent = strings.work_card_tracking_copied;
          },
          () => {
            trackingNotice.textContent = strings.state_error;
          },
        );
      });
      nodes.push(
        fact(strings.work_card_tracking_url, url),
        fact(strings.work_card_tracking_pin, pin),
        node('p', 'work-card__note', strings.work_card_tracking_once),
        copy,
      );
    }

    nodes.push(create, revoke, trackingNotice);
    sections.tracking.replaceChildren(...nodes);

    return true;
  }

  // ── Оценка: необязательна и ничего не блокирует (CR-014) ────────────────────

  let ratingDismissed = false;
  let ratingSending = false;

  async function sendRating(form: HTMLFormElement, notice: HTMLElement, score: string, comment: string): Promise<void> {
    if (ratingSending) {
      return;
    }

    ratingSending = true;

    try {
      const saved = await client.submitWorkRating(workId, { score: Number(score), comment: comment === '' ? null : comment });

      ratingSending = false;

      if (live() && work !== null) {
        setWork({ ...work, my_rating: saved });
        renderWork();
      }
    } catch (error) {
      ratingSending = false;

      if (live()) {
        showRefusal(form, notice, error, strings);
      }
    }
  }

  function renderRating(shown: Work): boolean {
    const rating = shown.my_rating;

    if (rating === null) {
      return false;
    }

    const nodes: HTMLElement[] = [node('h2', '', strings.work_card_rating)];

    if (rating.submitted_at !== null) {
      nodes.push(fact(strings.work_card_rating_score, String(rating.score ?? '')));

      if (rating.comment !== null) {
        nodes.push(fact(strings.work_card_rating_comment, rating.comment));
      }

      if (rating.late) {
        nodes.push(node('p', 'work-card__note', strings.work_card_rating_late));
      }
    } else if (ratingDismissed) {
      return false;
    } else {
      const form = document.createElement('form');
      const score = document.createElement('select');
      const comment = textArea('comment');
      const submit = node('button', 'action', strings.work_card_rating_send);
      const later = node('button', 'action', strings.work_card_rating_later);
      const notice = node('p', 'notice');
      const scores: HTMLOptionElement[] = [option('', '')];

      for (const value of RATING_SCORES) {
        scores.push(option(String(value), String(value)));
      }

      form.className = 'form';
      form.noValidate = true;
      form.dataset.form = 'rating';
      score.name = 'score';
      score.replaceChildren(...scores);
      submit.type = 'submit';
      later.type = 'button';
      later.addEventListener('click', () => {
        ratingDismissed = true;
        renderWork();
      });
      form.append(
        field(strings.work_card_rating_score, score),
        field(strings.work_card_rating_comment, comment),
        submit,
        later,
        notice,
      );
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        void sendRating(form, notice, score.value, comment.value);
      });
      nodes.push(form);
    }

    withDrafts(sections.rating, () => {
      sections.rating.replaceChildren(...nodes);
    });

    return true;
  }

  // ── Отмена и спор: только по `claim_actions` ────────────────────────────────

  let dispute: Dispute | null = null;
  let disputeFailure: string | null = null;
  let claimForm: 'stop' | 'dispute' | null = null;
  let claimBusy = false;
  const claimNotice = node('p', 'notice');

  claimNotice.setAttribute('role', 'alert');

  const has = (shown: Work, action: WorkClaimAction): boolean => shown.claim_actions.includes(action);

  async function readDispute(id: string): Promise<void> {
    try {
      const received = await client.getDispute(id);

      if (live()) {
        dispute = received;
        disputeFailure = null;
      }
    } catch (error) {
      if (live()) {
        disputeFailure = refusalText(error, strings);
      }
    }
  }

  /** Спор, названный работой, читается, если он ещё не на экране. */
  async function followDispute(): Promise<void> {
    const named = work?.dispute ?? null;

    // Нерешённый спор перечитывается: сообщения второй стороны и решение персонала.
    if (named != null && (dispute?.id !== named.id || dispute.resolved_at === null)) {
      await readDispute(named.id);
    }
  }

  async function rereadWork(): Promise<void> {
    try {
      const received = await client.getWork(workId);

      if (live()) {
        setWork(received);
        await followDispute();
      }
    } catch (error) {
      if (live()) {
        actionNotice.textContent = `${actionNotice.textContent ?? ''} ${refusalText(error, strings)}`.trim();
      }
    }
  }

  /** Действие над запросом отмены или спором: один запрос за раз, итог — текстом. */
  async function claimAct(act: () => Promise<void>, form?: HTMLElement, notice: HTMLElement = claimNotice): Promise<void> {
    if (claimBusy) {
      return;
    }

    claimBusy = true;
    claimNotice.textContent = '';

    try {
      await act();
    } catch (error) {
      if (live()) {
        if (form === undefined) {
          claimNotice.textContent = refusalText(error, strings);
          await rereadWork();
        } else {
          showRefusal(form, notice, error, strings);
          claimBusy = false;

          return;
        }
      }
    }

    claimBusy = false;

    if (live()) {
      renderWork();
    }
  }

  function claimButton(caption: string, handler: () => void, className = 'action'): HTMLButtonElement {
    const made = node('button', className, caption);

    made.type = 'button';
    made.addEventListener('click', handler);

    return made;
  }

  function applyAnswer(answer: () => Promise<Work>): void {
    void claimAct(async () => {
      const saved = await answer();

      if (live()) {
        setWork(saved);
        await followDispute();
      }
    });
  }

  function stopForm(shown: Work): HTMLElement {
    const form = document.createElement('form');
    const category = document.createElement('select');
    const reason = textArea('reason');
    const notice = node('p', 'notice');
    const submit = node('button', 'action', strings.work_card_cancel_send);
    const choices: HTMLOptionElement[] = [option('', '')];

    for (const value of shown.cancellation_categories) {
      choices.push(option(value, enumLabel(strings, cancellationCategoryStringKey(value))));
    }

    category.name = 'category';
    category.replaceChildren(...choices);
    submit.type = 'submit';
    form.className = 'form';
    form.noValidate = true;
    form.dataset.form = 'cancellation';
    form.append(
      field(strings.work_card_cancel_category, category),
      field(strings.work_card_cancel_reason, reason),
      submit,
      notice,
    );
    form.addEventListener('submit', (event) => {
      event.preventDefault();

      const chosen = category.value === ''
        ? { reason: reason.value }
        : { reason: reason.value, category: category.value as NonNullable<Work['cancellation_categories'][number]> };

      void claimAct(async () => {
        await client.requestWorkCancellation(workId, chosen);

        if (live()) {
          claimForm = null;
          await rereadWork();
        }
      }, form, notice);
    });

    return form;
  }

  function disputeForm(): HTMLElement {
    const form = document.createElement('form');
    const statement = textArea('statement');
    const notice = node('p', 'notice');
    const submit = node('button', 'action action--danger', strings.work_card_dispute_send);

    submit.type = 'submit';
    form.className = 'form';
    form.noValidate = true;
    form.dataset.form = 'dispute';
    form.append(field(strings.work_card_dispute_statement, statement), submit, notice);
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      void claimAct(async () => {
        const opened = await client.openWorkDispute(workId, statement.value);

        if (live()) {
          dispute = opened;
          claimForm = null;
          await rereadWork();
        }
      }, form, notice);
    });

    return form;
  }

  function disputeBlock(shown: Dispute): HTMLElement {
    const block = node('div', 'work-card__dispute');
    const locale = activeLocale();

    block.append(
      node('h3', '', strings.work_card_dispute),
      fact(strings.work_card_status, enumLabel(strings, disputeStatusStringKey(shown.status))),
      fact(strings.work_card_dispute_statement, shown.statement),
    );

    if (shown.outcome !== null) {
      block.append(fact(strings.work_card_dispute_outcome, enumLabel(strings, disputeOutcomeStringKey(shown.outcome))));
    }

    if (shown.guilty_side !== null) {
      block.append(fact(strings.work_card_dispute_guilty, enumLabel(strings, guiltySideStringKey(shown.guilty_side))));
    }

    if (shown.resolution_note !== null) {
      block.append(fact(strings.work_card_dispute_note, shown.resolution_note));
    }

    const list = node('ul', 'work-card__list');

    for (const message of shown.messages) {
      list.append(
        node(
          'li',
          'work-card__row',
          `${enumLabel(strings, disputeAuthorStringKey(message.author))} · ${momentTimeLabel(locale, message.created_at)} · ${message.body}`,
        ),
      );
    }

    block.append(list);

    // Закрытый спор сервер отмечает своим моментом решения.
    if (shown.resolved_at === null) {
      const form = document.createElement('form');
      const text = textArea('body');
      const notice = node('p', 'notice');
      const submit = node('button', 'action', strings.work_card_dispute_message_send);

      submit.type = 'submit';
      form.className = 'form';
      form.noValidate = true;
      form.dataset.form = 'dispute-message';
      form.append(field(strings.work_card_dispute_message, text), submit, notice);
      form.addEventListener('submit', (event) => {
        event.preventDefault();

        const sent = text.value;

        void claimAct(async () => {
          const saved = await client.postDisputeMessage(shown.id, sent);

          if (live() && dispute !== null) {
            dispute = { ...dispute, messages: [...dispute.messages, saved] };
            text.value = '';
          }
        }, form, notice);
      });
      block.append(form);
    }

    return block;
  }

  function renderClaims(shown: Work): boolean {
    const nodes: HTMLElement[] = [node('h2', '', strings.work_card_claims)];
    const request = shown.cancellation_request;
    let filled = false;

    if (request !== null) {
      filled = true;

      const items: HTMLElement[] = [
        fact(strings.work_card_status, enumLabel(strings, cancellationRequestStatusStringKey(request.status))),
        fact(strings.work_card_cancel_reason, request.reason),
      ];

      if (request.category !== null) {
        items.push(fact(strings.work_card_cancel_category, enumLabel(strings, cancellationCategoryStringKey(request.category))));
      }

      items.push(fact(strings.work_card_cancel_respond_by, momentTimeLabel(activeLocale(), request.respond_by), 'respond-by'));
      nodes.push(...items);

      if (has(shown, 'ACCEPT_CANCELLATION')) {
        nodes.push(claimButton(strings.work_card_cancel_accept, () => {
          if (window.confirm(strings.work_card_cancel_accept_question)) {
            applyAnswer(async () => await client.acceptCancellationRequest(request.id));
          }
        }));
      }

      if (has(shown, 'DECLINE_CANCELLATION')) {
        nodes.push(claimButton(strings.work_card_cancel_decline, () => {
          applyAnswer(async () => await client.declineCancellationRequest(request.id));
        }, 'action action--danger'));
      }

      if (has(shown, 'WITHDRAW_CANCELLATION')) {
        nodes.push(claimButton(strings.work_card_cancel_withdraw, () => {
          applyAnswer(async () => await client.withdrawCancellationRequest(request.id));
        }));
      }
    }

    if (has(shown, 'REQUEST_CANCELLATION')) {
      filled = true;
      nodes.push(claimForm === 'stop'
        ? stopForm(shown)
        : claimButton(strings.work_card_cancel_request, () => {
          claimForm = 'stop';
          renderWork();
        }));
    }

    if (has(shown, 'OPEN_DISPUTE')) {
      filled = true;
      nodes.push(claimForm === 'dispute'
        ? disputeForm()
        : claimButton(strings.work_card_dispute_open, () => {
          claimForm = 'dispute';
          renderWork();
        }, 'action action--danger'));
    }

    if (dispute !== null) {
      filled = true;
      nodes.push(disputeBlock(dispute));
    }

    if (disputeFailure !== null) {
      filled = true;
      nodes.push(node('p', 'notice', disputeFailure));
    }

    if (claimNotice.textContent !== '') {
      filled = true;
    }

    withDrafts(sections.claims, () => {
      sections.claims.replaceChildren(...nodes, claimNotice);
    });

    return filled;
  }

  // ── Сборка ──────────────────────────────────────────────────────────────────

  const backLink = node('a', 'company-link');
  const chatHeading = node('h2', '');
  const areaHead = node('div', 'work-card__head');
  const areaColumns = node('div', 'work-card__columns');
  const areaMain = node('div', 'work-card__main');
  const areaSide = node('div', 'work-card__side');
  const idLine = node('p', 'work-card__id');

  backLink.href = worksPath(companyId);
  areaColumns.append(areaMain, areaSide);

  function renderWork(): void {
    if (work === null) {
      return;
    }

    const shown = work;
    const inHead: HTMLElement[] = [];
    const inMain: HTMLElement[] = [];
    const inSide: HTMLElement[] = [];
    const place = (target: HTMLElement, area: HTMLElement[], present: boolean): void => {
      if (present) {
        area.push(target);
      }
    };

    renderStatus(shown);
    place(sections.head, inHead, true);
    place(sections.actions, inHead, renderActions(shown));
    place(sections.parties, inMain, renderParties(shown));
    renderAssignment(shown);
    place(sections.assignment, inMain, true);
    place(sections.cargo, inMain, renderCargo(shown));
    renderMapSection();
    place(sections.map, inMain, true);
    chatHeading.textContent = strings.work_card_chat;

    if (chatPane.root.parentElement !== sections.chat) {
      sections.chat.replaceChildren(chatHeading, chatPane.root);
    }

    place(sections.chat, inSide, true);
    place(sections.thread, inSide, renderThread(shown));
    place(sections.files, inMain, true);
    place(sections.photos, inMain, true);
    place(sections.tracking, inMain, renderTracking(shown));
    place(sections.rating, inSide, renderRating(shown));
    place(sections.claims, inMain, renderClaims(shown));
    renderHistory(shown);
    place(sections.history, inSide, true);

    backLink.textContent = strings.works_title;
    idLine.textContent = shown.id;
    placeSections(areaHead, inHead);
    placeSections(areaMain, inMain);
    placeSections(areaSide, inSide);
    placeSections(body, [backLink, areaHead, areaColumns, idLine]);

    if (body.parentElement !== element) {
      element.replaceChildren(body);
    }
  }

  /**
   * Разделы встают без перестановки стоящих: снятый со страницы узел останавливает
   * голосовое и сбрасывает прокрутку. Сначала уходит лишнее, затем вставляется новое.
   */
  function placeSections(parent: HTMLElement, wanted: readonly HTMLElement[]): void {
    for (const child of [...parent.children]) {
      if (!wanted.includes(child as HTMLElement)) {
        child.remove();
      }
    }

    let cursor = parent.firstElementChild;

    for (const item of wanted) {
      if (item === cursor) {
        cursor = cursor.nextElementSibling;
      } else {
        parent.insertBefore(item, cursor);
      }
    }
  }

  function renderFailure(): void {
    if (state === 'noAccess') {
      element.replaceChildren(node('p', 'screen-state screen-state--noAccess', strings.work_card_not_available));

      return;
    }

    const again = node('button', 'action', strings.state_retry);

    again.type = 'button';
    again.addEventListener('click', () => {
      void load();
    });
    element.replaceChildren(renderScreenState('error', strings), again);
  }

  async function load(): Promise<void> {
    state = screenStateFor({ kind: 'pending' });
    work = null;
    element.replaceChildren(renderScreenState('loading', strings));

    let received: Work;

    try {
      received = await client.getWork(workId);
    } catch (error) {
      if (!live()) {
        return;
      }

      const code = error instanceof ApiFailure ? error.code : null;

      state = screenStateFor({ kind: 'failed', code });
      renderFailure();

      return;
    }

    if (!live()) {
      return;
    }

    setWork(received);
    state = null;
    mapPlace.hidden = true;
    renderFiles();
    renderPhotos();
    renderWork();
    timer ??= setInterval(() => {
      void tick();
    }, TRACK_PERIOD_MS);

    await Promise.all([attachMap(), readTrack(), chatPane.read(), readFiles(), readPhotos(), followDispute().then(() => {
      if (live()) {
        renderWork();
      }
    })]);
  }

  function relabel(next: Strings): void {
    strings = next;

    for (const apply of relabels) {
      apply();
    }

    if (work !== null) {
      renderFiles();
      renderPhotos();
      renderWork();
    } else if (state !== null && state !== 'loading') {
      renderFailure();
    }
  }

  function dispose(): void {
    disposed = true;

    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }

    map?.destroy();
    map = null;
  }

  element.replaceChildren(renderScreenState('loading', strings));

  return {
    element,

    get state(): ScreenState | null {
      return state;
    },

    load,
    relabel,
    dispose,
  };
}
